/**
 * The writing-assessment marking screen — the heart of the old Power App.
 * Enter/choose a Test ID → see the writing question, hint and the candidate's
 * answer → mark Satisfied / Dissatisfied (+ mandatory comment when Dissatisfied)
 * → the API builds the PDF report, saves the SharePoint copy and emails it.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { EvalLookup } from "../api/types";
import { fmtDate } from "../lib/format";

export default function EvaluatePage() {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [testIdInput, setTestIdInput] = useState(params.get("testId") ?? "");
  const [lookup, setLookup] = useState<EvalLookup | null>(null);
  const [warning, setWarning] = useState("");
  const [result, setResult] = useState<"satisfied" | "dissatisfied" | "">("");
  const [comment, setComment] = useState("");
  const [done, setDone] = useState<null | { email_ok?: boolean; report_upload_ok?: boolean }>(null);

  const { data: queue } = useQuery({
    queryKey: ["evalQueue"],
    queryFn: () => api.assessments.list({ status: "submitted" }),
  });

  const search = async (tid: string) => {
    setWarning("");
    setLookup(null);
    setDone(null);
    setResult("");
    setComment("");
    if (!tid.trim()) return;
    try {
      const r = await api.assessments.byTestId(tid.trim());
      if (r.status === "evaluated") {
        setWarning("This answer has already been assessed.");
        setLookup(r);
      } else if (r.status !== "submitted") {
        setWarning("The candidate hasn't submitted this assessment yet.");
      } else {
        setLookup(r);
      }
    } catch (err) {
      setWarning(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "This Assessment ID doesn't exist");
    }
  };

  // auto-run when arriving with ?testId=
  useEffect(() => {
    const t = params.get("testId");
    if (t) void search(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const evaluateMut = useMutation({
    mutationFn: () =>
      api.assessments.evaluate(lookup!.id, {
        result: result as "satisfied" | "dissatisfied",
        comment,
      }),
    onSuccess: (res) => {
      setDone({ email_ok: res.email_ok, report_upload_ok: res.report_upload_ok });
      qc.invalidateQueries({ queryKey: ["evalQueue"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const submit = () => {
    setWarning("");
    if (!result) {
      setWarning("Evaluate the answer first.");
      return;
    }
    if (result === "dissatisfied" && !comment.trim()) {
      setWarning("You need to provide an explanation in the comment.");
      return;
    }
    evaluateMut.mutate();
  };

  if (done && lookup) {
    return (
      <div className="max-w-lg mx-auto text-center card p-8 space-y-4">
        <div className="w-14 h-14 mx-auto rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
          <span className="text-liaise-green text-2xl">✓</span>
        </div>
        <h1 className="text-xl font-bold text-liaise-navy">Evaluation saved</h1>
        <p className="text-sm text-slate-500">
          The report for <strong>{lookup.candidate}</strong> has been generated
          {done.report_upload_ok ? ", saved to SharePoint" : ""}
          {done.email_ok ? " and emailed" : ""}.
        </p>
        {(done.report_upload_ok === false || done.email_ok === false) && (
          <p className="text-xs text-liaise-orange">
            {done.report_upload_ok === false && "The SharePoint copy could not be saved. "}
            {done.email_ok === false && "The email could not be sent. "}
            The evaluation is saved. You can view and download the PDF from the assessment record. Contact recruitment about email or SharePoint delivery.
          </p>
        )}
        <div className="flex justify-center gap-3 pt-2">
          <Link to={`/assessment/${lookup.id}`} className="btn-secondary">View record</Link>
          <button className="btn-primary" onClick={() => { setLookup(null); setDone(null); setTestIdInput(""); }}>
            Evaluate another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-liaise-navy">Writing assessment</h1>
        <p className="text-sm text-slate-500">Look up a Test ID, or pick from the queue below.</p>
      </div>

      <div className="card p-4 flex gap-3 items-end flex-wrap">
        <div className="grow max-w-xs">
          <label className="label">Assessment Test ID</label>
          <input
            className="input font-mono"
            placeholder="e.g. RA-4F7K2M"
            value={testIdInput}
            onChange={(e) => setTestIdInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && search(testIdInput)}
          />
        </div>
        <button className="btn-primary" onClick={() => search(testIdInput)}>Search</button>
        {warning && <p className="text-liaise-red text-sm basis-full">{warning}</p>}
      </div>

      {lookup && lookup.status === "submitted" && (
        <div className="card p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
            <div>
              <span className="font-bold text-liaise-navy">{lookup.candidate}</span>
              <span className="text-slate-500"> · {lookup.home ?? "-"} · {lookup.position ?? "-"} · {fmtDate(lookup.assessment_date)}</span>
            </div>
            <span className="font-mono text-xs text-slate-400">{lookup.test_id}</span>
          </div>

          <div>
            <div className="label">Question</div>
            <p className="text-sm text-slate-800">{lookup.writing_question ?? "-"}</p>
          </div>

          {lookup.writing_passage && (
            <div className="border border-slate-200 bg-slate-50 rounded-lg p-4">
              <div className="label">The paragraph the candidate read</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{lookup.writing_passage}</p>
            </div>
          )}

          {lookup.writing_hint && (
            <div className="bg-liaise-navy-50 border border-liaise-navy-200 rounded-lg p-3">
              <div className="label">What to look for</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{lookup.writing_hint}</p>
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="label mb-0">Candidate's answer</div>
              {(lookup.paste_count ?? 0) > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                  Text was pasted {lookup.paste_count}x
                </span>
              )}
              {(lookup.focus_loss_count ?? 0) >= 5 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                  Left the test window {lookup.focus_loss_count}x
                </span>
              )}
              {lookup.duration_seconds != null && (
                <span className="text-xs text-slate-400">
                  completed in {Math.floor(lookup.duration_seconds / 60)}m {lookup.duration_seconds % 60}s
                </span>
              )}
            </div>
            <div className="border border-slate-200 rounded-lg p-4 text-sm whitespace-pre-wrap bg-white min-h-[120px]">
              {lookup.writing_answer?.trim() || "(no answer provided)"}
            </div>
          </div>

          <div className="flex gap-6 items-center">
            <span className="label mb-0">Your evaluation</span>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="result" checked={result === "satisfied"} onChange={() => setResult("satisfied")} />
              <span className="font-semibold text-liaise-green">Satisfied</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="radio" name="result" checked={result === "dissatisfied"} onChange={() => setResult("dissatisfied")} />
              <span className="font-semibold text-liaise-red">Dissatisfied</span>
            </label>
          </div>

          <div>
            <div className="label">
              Comment {result === "dissatisfied" && <span className="text-liaise-red normal-case">(required)</span>}
            </div>
            <textarea
              className="input min-h-[90px]"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Notes on the candidate's written answer..."
            />
          </div>

          {evaluateMut.error && <p className="text-liaise-red text-sm">{String(evaluateMut.error)}</p>}

          <div className="flex justify-end">
            <button className="btn-primary" onClick={submit} disabled={evaluateMut.isPending}>
              {evaluateMut.isPending ? "Generating report..." : "Submit evaluation"}
            </button>
          </div>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="bg-liaise-navy text-white px-5 py-2.5 text-sm font-semibold">
          Awaiting evaluation {queue ? `(${queue.length})` : ""}
        </div>
        {(queue ?? []).length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">Nothing waiting - all caught up.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {queue!.map((r) => (
              <button
                key={r.id}
                className="w-full text-left px-5 py-3 hover:bg-liaise-navy-50/60 flex items-center justify-between gap-3 text-sm"
                onClick={() => {
                  setTestIdInput(r.test_id);
                  void search(r.test_id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                <span>
                  <span className="font-semibold text-liaise-navy">{r.first_name} {r.last_name}</span>
                  <span className="text-slate-500"> · {r.home ?? "-"} · {fmtDate(r.assessment_date)}</span>
                </span>
                <span className="font-mono text-xs text-slate-400">{r.test_id}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
